import { useTranslation } from 'react-i18next';

import { useGetMyEloHistoryQuery, useGetMyStatsQuery } from '@/services/qgammonApi';
import {
  andamentoElo,
  statisticheVuote,
  vociStatistiche,
  type PuntoElo,
  type Voce,
} from '@/lib/qgammon/statistiche';
import type { ListVerdict } from '@/lib/listState';
import { PanelNotice, PanelWaiting } from '@/components/hub/PanelNotice';
import { useQgammonRitmo, useQgammonVerdetto, type QgammonTentativo } from '@/hooks/useQgammonLista';
import { cn } from '@/lib/cn';

/** Le righe di storico del grafico: quante ne disegna la reference s3. */
const STORIA_LIMIT = 30;

/** `/me/stats` risponde `404 not_found` a chi non ha ancora una riga in QGammon: non ha mai giocato. */
const NESSUNA_RIGA = { played: 0 } as const;

/**
 * Il 404 del CONTRATTO — `{ error: 'not_found' }`, «non hai una riga» — e nessun altro.
 * ⛔ Un 404 di ROTTA non e' un vuoto: un QGammon senza #79 (la prod del 26/09, `090eb502c8`, misurata
 * dal revisore di #402) risponde `{"error":"Not Found",…}` di Fastify, e leggerlo come «zero partite»
 * affermava «Nessuna partita» su una domanda che quel server non sa nemmeno ricevere. Stesso schema
 * di `isNotLinked` in `decks/AdvisorOverlay.tsx`: stato E corpo.
 */
function nonTrovato(error: unknown): boolean {
  const e = error as { status?: unknown; data?: { error?: unknown } } | null | undefined;
  return e?.status === 404 && e?.data?.error === 'not_found';
}

type Classe = 'dati' | 'attesa' | 'vuoto' | 'errore';
const classe = (v: ListVerdict): Classe =>
  v === 'rows' ? 'dati' : v === 'loading' || v === 'slow' ? 'attesa' : v === 'empty' ? 'vuoto' : 'errore';

/**
 * La scheda «Statistiche» della Hub backgammon — variante **s3** scelta da Luca il 25/09 («hub
 * statistiche S3»), immagine `attachments/2026-09-24-backgammon-hub-statistiche-s3.jpg` del KB:
 * il grafico dell'andamento ELO in testa, sotto le voci di `/me/stats` in due colonne.
 *
 * Due fonti indipendenti, e ognuna dice il proprio stato: il grafico (`/me/elo-history`) e le voci
 * (`/me/stats`). Quando le due sono nello STESSO caso (entrambe in attesa, entrambe vuote, entrambe
 * in errore) il pannello lo dice una volta sola, non due volte una sotto l'altra.
 *  - vuoto = «nessuna partita»: `played === 0`, o il `404` di chi non ha mai giocato. Chi ha giocato
 *    solo col bot ha le voci ma non il grafico (le partite col bot non scrivono ELO), e il grafico
 *    lo dice per conto suo;
 *  - errore = la frase dei pannelli, con «Riprova» che rilancia la lettura che e' mancata.
 * ⛔ Nessun numero dall'immagine: min e max del grafico e ogni voce vengono dalla risposta, e una
 * voce che il server non da' non si scrive (`lib/qgammon/statistiche.ts`).
 */
export function QgammonStatsPanel() {
  const { t } = useTranslation('backgammon');

  const ritmoStats = useQgammonRitmo();
  const stats = useGetMyStatsQuery(undefined, ritmoStats.opts);
  const ritmoStoria = useQgammonRitmo();
  const storia = useGetMyEloHistoryQuery(STORIA_LIMIT, ritmoStoria.opts);

  // Il 404 e' una RISPOSTA («non hai una riga»), non un guasto: si legge come «zero partite».
  const statsNonTrovate = nonTrovato(stats.error);
  const statsQ: QgammonTentativo = statsNonTrovate
    ? {
        data: NESSUNA_RIGA,
        isLoading: false,
        isError: false,
        startedTimeStamp: stats.startedTimeStamp,
        fulfilledTimeStamp: stats.startedTimeStamp,
      }
    : stats;
  const statsData: unknown = statsQ.data;
  const voci = vociStatistiche(statsData);
  const vuote = statisticheVuote(statsData);
  const nVoci = vuote ? 0 : voci.sinistra.length + voci.destra.length;
  const verdettoStats = useQgammonVerdetto(ritmoStats, statsQ, nVoci);
  let vStats = classe(verdettoStats);
  // Una risposta senza nessuna voce leggibile e senza `played: 0` non e' un vuoto: e' una risposta
  // che non so leggere, e il vuoto non si afferma.
  if (vStats === 'vuoto' && !vuote) vStats = 'errore';

  const punti = andamentoElo(storia.data?.points ?? []);
  const verdettoStoria = useQgammonVerdetto(ritmoStoria, storia, punti.length);
  const vStoria = classe(verdettoStoria);

  const riprovaStats = () => void stats.refetch();
  const riprovaStoria = () => void storia.refetch();

  if (vStats === vStoria && vStats !== 'dati') {
    return (
      <div className="flex min-h-0 flex-1 flex-col" data-testid="bg-stats" data-stato={vStats}>
        {vStats === 'attesa' ? (
          <PanelWaiting slow={verdettoStats === 'slow' || verdettoStoria === 'slow'} />
        ) : vStats === 'vuoto' ? (
          <Vuoto testo={t('hubPanels.matchesEmpty', { defaultValue: 'No matches yet — play your first game!' })} />
        ) : (
          <PanelNotice
            tone="error"
            // Spento solo se NESSUNA delle due si puo' rilanciare: una lettura in volo non riparte
            // (RTK: `refetch` su una richiesta `pending` non fa niente), l'altra si'.
            retrying={stats.isFetching && storia.isFetching}
            onRetry={() => {
              riprovaStats();
              riprovaStoria();
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-contain py-2"
      data-testid="bg-stats"
      data-stato="sezioni"
    >
      <section
        data-testid="bg-stats-grafico"
        data-stato={vStoria}
        className="card-edge shrink-0 rounded-2xl bg-bg-secondary p-4"
      >
        <p className="text-sm font-semibold text-fg-muted">
          {t('stats.eloTrend', { defaultValue: 'ELO trend' })}
        </p>
        {vStoria === 'dati' ? (
          <GraficoElo punti={punti} />
        ) : vStoria === 'attesa' ? (
          <PanelWaiting slow={verdettoStoria === 'slow'} className="h-40" />
        ) : vStoria === 'vuoto' ? (
          <p className="py-8 text-center text-sm text-fg-muted">
            {t('stats.noRanked', { defaultValue: 'No ranked matches yet: the trend starts with your first.' })}
          </p>
        ) : (
          <PanelNotice tone="error" onRetry={riprovaStoria} retrying={storia.isFetching} className="h-auto py-4" />
        )}
      </section>

      <section data-testid="bg-stats-voci" data-stato={vStats} className="shrink-0">
        {vStats === 'dati' ? (
          <div className="grid grid-cols-2 gap-2">
            <Colonna voci={voci.sinistra} testid="bg-stats-sinistra" />
            <Colonna voci={voci.destra} testid="bg-stats-destra" />
          </div>
        ) : vStats === 'attesa' ? (
          <PanelWaiting slow={verdettoStats === 'slow'} className="h-auto py-4" />
        ) : vStats === 'vuoto' ? (
          <Vuoto testo={t('hubPanels.matchesEmpty', { defaultValue: 'No matches yet — play your first game!' })} />
        ) : (
          <PanelNotice tone="error" onRetry={riprovaStats} retrying={stats.isFetching} className="h-auto py-4" />
        )}
      </section>
    </div>
  );
}

function Vuoto({ testo }: { testo: string }) {
  return <p className="rounded-xl bg-bg-secondary p-4 text-center text-sm text-fg-muted">{testo}</p>;
}

function Colonna({ voci, testid }: { voci: Voce[]; testid: string }) {
  const { t } = useTranslation('backgammon');
  return (
    <ul className="flex min-w-0 flex-col gap-2" data-testid={testid}>
      {voci.map((v) => (
        <li
          key={v.chiave}
          data-voce={v.chiave}
          className="flex items-center justify-between gap-2 rounded-full bg-bg-secondary px-4 py-2.5"
        >
          <span className="min-w-0 truncate text-sm text-fg">{t(`stats.voci.${v.chiave}`)}</span>
          <Valore voce={v} />
        </li>
      ))}
    </ul>
  );
}

function Valore({ voce }: { voce: Voce }) {
  const { t } = useTranslation('backgammon');
  const base = 'shrink-0 text-lg font-bold tabular-nums';
  const v = voce.valore;
  switch (v.tipo) {
    case 'numero':
      return (
        <span
          className={cn(base, v.tono === 'vinte' ? 'text-success' : v.tono === 'perse' ? 'text-danger' : 'text-fg')}
        >
          {v.n}
        </span>
      );
    case 'coppia':
      return (
        <span className={base} title={t('stats.coppiaHint', { defaultValue: 'won / lost' })}>
          <span className="text-success">{v.vinte}</span>
          <span className="text-fg-muted"> / </span>
          <span className="text-danger">{v.perse}</span>
        </span>
      );
    case 'su':
      return (
        <span
          className={cn(base, 'text-fg')}
          title={t('stats.suHint', { won: v.vinte, played: v.giocate, defaultValue: '{{won}} won of {{played}}' })}
        >
          {v.vinte}/{v.giocate}
        </span>
      );
    case 'pr':
      return v.partite === undefined ? (
        <span className={cn(base, 'text-fg')}>{v.pr.toFixed(1)}</span>
      ) : (
        <span className={cn(base, 'text-fg', 'flex flex-col items-end leading-tight')}>
          <span>{v.pr.toFixed(1)}</span>
          <span className="text-[11px] font-normal text-fg-muted">
            {t('stats.prMatches', { count: v.partite })}
          </span>
        </span>
      );
  }
}

/** Geometria del grafico, in unita' del `viewBox` (si scala in larghezza, le proporzioni restano). */
const W = 300;
const H = 140;
const PAD = 8;

/**
 * L'andamento ELO: una serie sola, quindi nessuna legenda — il titolo la nomina. Linea di 2 px e
 * area sotto nel colore del marchio; il punto piu' recente e' pieno e piu' grande. Le compensazioni
 * dei match annullati (`void`) sono punti VUOTI, e la riga che lo spiega compare solo se ce n'e' uno.
 * min e max sono quelli dei punti disegnati.
 */
function GraficoElo({ punti }: { punti: PuntoElo[] }) {
  const { t } = useTranslation('backgammon');
  const valori = punti.map((p) => p.elo);
  const min = Math.min(...valori);
  const max = Math.max(...valori);
  // Una serie piatta non ha ampiezza: la si disegna a meta' altezza invece di dividere per zero.
  const ampiezza = max - min || 1;
  const x = (i: number) => (punti.length === 1 ? W / 2 : PAD + (i * (W - 2 * PAD)) / (punti.length - 1));
  const y = (elo: number) => (max === min ? H / 2 : PAD + ((max - elo) * (H - 2 * PAD)) / ampiezza);
  const linea = punti.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.elo).toFixed(1)}`).join(' ');
  const area = `${linea} L${x(punti.length - 1).toFixed(1)},${H} L${x(0).toFixed(1)},${H} Z`;
  const ultimo = punti.length - 1;
  const conAnnullati = punti.some((p) => p.annullato);

  return (
    <div className="mt-2">
      <div className="flex items-baseline justify-end gap-1 text-fg-muted">
        <span className="text-[11px]">{t('stats.eloMax', { defaultValue: 'max ELO' })}</span>
        <span data-testid="bg-stats-max" className="text-sm font-bold tabular-nums text-fg">
          {max}
        </span>
      </div>
      <svg
        role="img"
        aria-label={t('stats.eloChartLabel', {
          from: valori[0],
          to: valori[ultimo],
          defaultValue: 'ELO from {{from}} to {{to}}',
        })}
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full text-brand"
        data-testid="bg-stats-svg"
      >
        <defs>
          <linearGradient id="bg-stats-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity={0.3} />
            <stop offset="100%" stopColor="currentColor" stopOpacity={0} />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#bg-stats-area)" />
        <path d={linea} fill="none" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {punti.map((p, i) => (
          <circle
            key={i}
            data-annullato={p.annullato || undefined}
            cx={x(i)}
            cy={y(p.elo)}
            r={i === ultimo ? 4.5 : 3}
            strokeWidth={1.5}
            className={p.annullato ? 'fill-bg-secondary stroke-fg-muted' : 'fill-current stroke-bg-secondary'}
          >
            <title>{p.elo}</title>
          </circle>
        ))}
      </svg>
      <div className="flex items-baseline gap-1 text-fg-muted">
        <span className="text-[11px]">{t('stats.eloMin', { defaultValue: 'min ELO' })}</span>
        <span data-testid="bg-stats-min" className="text-sm font-bold tabular-nums text-fg">
          {min}
        </span>
      </div>
      {conAnnullati && (
        <p data-testid="bg-stats-annullati" className="mt-1 text-[11px] text-fg-muted">
          {t('stats.voidLegend', { defaultValue: '○ result voided by an admin' })}
        </p>
      )}
    </div>
  );
}
